import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CustomerDetail } from '@/lib/api/customers';
import { DateDisplay } from '@/components/date-display';
import { Monitor, Globe, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DeviceTypeIcon, BrowserIcon, OSIcon, isInAppBrowser, InAppBrowserWarning, getBrowserLabel, getOSLabel } from '@/components/ui/device-icons';

interface CustomerDevicesProps {
  customer: CustomerDetail;
}

type Device = {
  id: string;
  customerId?: string | null;
  browser?: string | null;
  browserVersion?: string | null;
  os?: string | null;
  osVersion?: string | null;
  userAgent?: string | null;
  deviceType?: string | null;
  deviceModel?: string | null;
  screenWidth?: number | null;
  screenHeight?: number | null;
  timeZone?: string | null;
  browserLocale?: string | null;
  queryLocale?: string | null;
  queryCurrency?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  geoIpAddress?: string | null;
  geoCity?: string | null;
  geoCityGeonameId?: number | null;
  geoRegion?: string | null;
  geoRegionIsoCode?: string | null;
  geoRegionGeonameId?: number | null;
  geoPostalCode?: string | null;
  geoCountry?: string | null;
  geoCountryCode?: string | null;
  geoCountryGeonameId?: number | null;
  geoCountryIsEu?: boolean | null;
  geoContinent?: string | null;
  geoContinentCode?: string | null;
  geoContinentGeonameId?: number | null;
  geoLatitude?: string | null;
  geoLongitude?: string | null;
  geoIsVpn?: boolean | null;
  geoTimezoneName?: string | null;
  geoTimezoneAbbreviation?: string | null;
  geoTimezoneGmtOffset?: number | null;
  geoTimezoneCurrentTime?: string | null;
  geoTimezoneIsDst?: boolean | null;
  geoCurrencyName?: string | null;
  geoCurrencyCode?: string | null;
  geoAsn?: number | null;
  geoAso?: string | null;
  geoConnectionType?: string | null;
  geoIspName?: string | null;
  geoOrgName?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  lastSeenAt?: Date | null;
};

// Helper function to get device icon based on device type or screen size
function getDeviceIcon(device: Device) {
  // Determine device type from explicit type or screen size
  let deviceType = device.deviceType || 'unknown';

  if (!device.deviceType) {
    // Fallback to screen size
    if (device.screenWidth && device.screenWidth < 768) {
      deviceType = 'mobile';
    } else if (device.screenWidth && device.screenWidth < 1024) {
      deviceType = 'tablet';
    } else {
      deviceType = 'desktop';
    }
  }

  return <DeviceTypeIcon deviceType={deviceType} className="h-4 w-4" />;
}

function CustomerDevices({ customer }: CustomerDevicesProps) {
  const { t } = useTranslation('customers');
  const devices = ((customer as any).devices as Device[]) || [];

  if (!devices || devices.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            {t('devices.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-sm">{t('devices.empty')}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Monitor className="h-5 w-5" />
          {t('devices.titleWithCount', { count: devices.length })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {devices.map((device: Device, index: number) => (
            <div
              key={device.id}
              className={`rounded-lg border p-4 ${index === 0 ? 'bg-muted/50' : ''}`}
            >
              {/* Device Header */}
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {getDeviceIcon(device)}
                  <div>
                    <div className="flex items-center gap-2">
                      <BrowserIcon browser={device.browser || 'unknown'} className="h-4 w-4" />
                      <span className="font-medium">
                        {getBrowserLabel(device.browser || 'Unknown Browser')}{' '}
                        {device.browserVersion && `v${device.browserVersion}`}
                      </span>
                      {device.browser && isInAppBrowser(device.browser) && (
                        <InAppBrowserWarning />
                      )}
                      {device.deviceType && (
                        <Badge variant="secondary" className="text-xs capitalize">
                          {device.deviceType}
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted-foreground text-sm flex items-center gap-1.5">
                      <OSIcon os={device.os || 'unknown'} className="h-3.5 w-3.5" />
                      {getOSLabel(device.os || 'Unknown OS')} {device.osVersion && `${device.osVersion}`}
                    </div>
                  </div>
                </div>
                {index === 0 && (
                  <Badge variant="default" className="text-xs">
                    {t('devices.primary')}
                  </Badge>
                )}
              </div>

              {/* Device Details Grid */}
              <div className="grid gap-3 text-sm md:grid-cols-2">
                {/* Location */}
                {device.geoCountry && (
                  <div className="flex items-start gap-2">
                    <Globe className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div className="w-full">
                      <div className="text-muted-foreground text-xs">{t('devices.location')}</div>
                      <div className="flex items-center gap-2">
                        <span>
                          {device.geoCity && `${device.geoCity}, `}
                          {device.geoRegion && `${device.geoRegion}, `}
                          {device.geoCountry}
                        </span>
                        {device.geoCountryCode && (
                          <Badge variant="outline" className="text-xs">
                            {device.geoCountryCode}
                          </Badge>
                        )}
                        {device.geoCountryIsEu && (
                          <Badge variant="outline" className="text-xs">
                            EU
                          </Badge>
                        )}
                      </div>
                      {device.geoPostalCode && (
                        <div className="text-muted-foreground text-xs">{t('devices.postal', { code: device.geoPostalCode })}</div>
                      )}
                      {(device.geoLatitude || device.geoLongitude) && (
                        <div className="text-muted-foreground text-xs">
                          {t('devices.coords', { lat: device.geoLatitude ?? '0.0', lng: device.geoLongitude ?? '0.0' })}
                        </div>
                      )}
                      {device.geoIsVpn && (
                        <Badge variant="destructive" className="mt-1 text-xs">
                          {t('devices.vpnDetected')}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Device Model */}
                {device.deviceModel && (
                  <div className="flex items-start gap-2">
                    <Monitor className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.deviceModel')}</div>
                      <div>{device.deviceModel}</div>
                    </div>
                  </div>
                )}

                {/* Screen Resolution */}
                {device.screenWidth && device.screenHeight && (
                  <div className="flex items-start gap-2">
                    <Monitor className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.screenResolution')}</div>
                      <div>
                        {device.screenWidth} × {device.screenHeight}
                      </div>
                    </div>
                  </div>
                )}

                {/* Timezone */}
                {device.timeZone && (
                  <div className="flex items-start gap-2">
                    <Clock className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.timezone')}</div>
                      <div>{device.timeZone}</div>
                    </div>
                  </div>
                )}

                {/* Locale */}
                {device.browserLocale && (
                  <div className="flex items-start gap-2">
                    <Globe className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.browserLocale')}</div>
                      <div>{device.browserLocale}</div>
                    </div>
                  </div>
                )}

                {/* IP Address */}
                {device.geoIpAddress && (
                  <div className="flex items-start gap-2">
                    <Globe className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.ipAddress')}</div>
                      <div className="font-mono text-xs">{device.geoIpAddress}</div>
                    </div>
                  </div>
                )}

                {/* ISP */}
                {device.geoAso && (
                  <div className="flex items-start gap-2">
                    <Globe className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.ispNetwork')}</div>
                      <div>{device.geoAso}</div>
                      {device.geoAsn && (
                        <div className="text-muted-foreground text-xs">{t('devices.asn', { asn: device.geoAsn })}</div>
                      )}
                      {device.geoIspName && (
                        <div className="text-muted-foreground text-xs">{t('devices.provider', { provider: device.geoIspName })}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Currency */}
                {device.geoCurrencyCode && (
                  <div className="flex items-start gap-2">
                    <Globe className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.localCurrency')}</div>
                      <div>
                        {device.geoCurrencyCode}
                        {device.geoCurrencyName && ` (${device.geoCurrencyName})`}
                      </div>
                    </div>
                  </div>
                )}

                {/* Timezone Details */}
                {device.geoTimezoneName && (
                  <div className="flex items-start gap-2">
                    <Clock className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">{t('devices.timezoneDetails')}</div>
                      <div>{device.geoTimezoneName}</div>
                      {device.geoTimezoneAbbreviation && (
                        <div className="text-muted-foreground text-xs">
                          {device.geoTimezoneAbbreviation} (GMT
                          {device.geoTimezoneGmtOffset !== null &&
                            device.geoTimezoneGmtOffset !== undefined &&
                            (device.geoTimezoneGmtOffset >= 0 ? '+' : '')}
                          {device.geoTimezoneGmtOffset})
                          {device.geoTimezoneIsDst && ` - ${t('devices.dstActive')}`}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* UTM Parameters */}
              {(device.utmSource || device.utmMedium || device.utmCampaign) && (
                <div className="mt-3 rounded border-t pt-3">
                  <div className="text-muted-foreground mb-2 text-xs font-medium">
                    {t('devices.utmParameters')}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {device.utmSource && (
                      <Badge variant="secondary" className="text-xs">
                        {t('devices.source', { value: device.utmSource })}
                      </Badge>
                    )}
                    {device.utmMedium && (
                      <Badge variant="secondary" className="text-xs">
                        {t('devices.medium', { value: device.utmMedium })}
                      </Badge>
                    )}
                    {device.utmCampaign && (
                      <Badge variant="secondary" className="text-xs">
                        {t('devices.campaign', { value: device.utmCampaign })}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-xs">
                {device.lastSeenAt && (
                  <div>
                    <span className="font-medium">{t('devices.lastSeen')}</span>{' '}
                    <DateDisplay value={device.lastSeenAt} format="MM/dd/yyyy HH:mm" />
                  </div>
                )}
                {device.createdAt && (
                  <div>
                    <span className="font-medium">{t('devices.firstSeen')}</span>{' '}
                    <DateDisplay value={device.createdAt} format="MM/dd/yyyy HH:mm" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default CustomerDevices;

